/**
 * Arquero runtime adapter — the arquero boundary.
 *
 * This is the only file in the engine that imports arquero. The
 * evaluator, the op registry, the type system, and the error model all
 * operate on the abstract `Dataset` value kind and never touch an arquero
 * `ColumnTable` directly. Top-level Dataset values entering verb apply
 * handlers are converted to a `ColumnTable` via `toTable`, processed by
 * arquero, and converted back via `fromTable`.
 *
 * `compileExpr` is the expression-context compiler: it walks an AST node
 * and produces a per-row JS function (`RowFn`) suitable for arquero's
 * `filter` / `derive` verbs. Each scalar/comparison/boolean op contributes
 * its own `compileExpr` method via `EagerOpMeta` — this file dispatches to
 * those methods.
 */

import type { ColumnTable } from 'arquero';
import { desc as aqDesc, escape as aqEscape, not as aqNot, table as aqTable } from 'arquero';
import { median, quantile, standardDeviation, variance } from 'simple-statistics';

import type { FillDirection, SortDirection } from '../../../schema/defs.ts';
import {
  cumulative,
  discreteDifference,
  type EwmaDecay,
  exponentialMovingAverage,
  rateOfChange,
  rollingSlope,
  rollingWindow,
  trapezoidIntegral,
  type WindowExtent,
} from '../../stats/series-window.ts';
import type { Unit } from '../../units/unit.ts';
import type { SourcePos } from '../ast.ts';
import type { Env } from '../env.ts';
import { lookup } from '../env.ts';
import {
  LambdaCallArityError,
  NestedDatasetCellError,
  NotAnExpressionOpError,
  ProjectorArityError,
  ShapeError,
  TypeMismatchError,
  UnknownOpError,
} from '../errors.ts';
import { type AstInput, foldAst, nodePos, opCallPos, type Spans } from '../normalize.ts';
import { type AggSpec, type CompileFn, type NameTable, opMeta, type RowFn, resolveOpCall } from '../op-meta.ts';
import { toDateValue } from '../ops/transform/date-utils.ts';
import type { JoinType } from '../ops/transform/join.ts';
import { elemPath, mapValuePath, paramPath } from '../path.ts';
import { refineSchema } from '../schema/refine.ts';
import type { IdentityTable } from '../schema/row-identity.ts';
import { type Schema, unknownSchemaFor } from '../schema/types.ts';
import {
  boxNumericCell,
  type Cell,
  type Columns,
  cellNumber,
  Dataset,
  describeValueKind,
  type IdentityAxis,
  isLambda,
  isUnitCell,
  Keyword,
  type LambdaValue,
  MapValue,
  MaterializedDataset,
  numericScalarOf,
  type RowIdentity,
  unifiedUnitOfCells,
  unitCell,
  type Value,
} from '../value.ts';
import { partitionByKeys } from './partition.ts';

/**
 * Synthetic per-row unit companion columns — the ONLY place a sidecar
 * representation of unit-bearing cells exists. A `UnitCell` cannot cross
 * into arquero intact (sorts, join keys, and row expressions need the bare
 * number), so `toTable` splits every boxed column into its plain-number
 * column plus a reserved `__cog_unit__<col>` companion carrying the
 * row-aligned units, and `fromTable` re-boxes and strips. Companions ride
 * verbs as ordinary columns — filter / orderby / slice / derive / join all
 * carry unlisted columns through row-aligned, so a value column and its
 * companion survive (or drop) together. Like {@link ROW_INDEX_COL}, the
 * prefix is exotic enough that no user-projectable column collides.
 * Outside this file: boxed cells in, boxed cells out.
 */
const UNIT_COL_PREFIX = '__cog_unit__';
const unitColName = (name: string): string => `${UNIT_COL_PREFIX}${name}`;
const isUnitColName = (name: string): boolean => name.startsWith(UNIT_COL_PREFIX);
const valueColName = (unitCol: string): string => unitCol.slice(UNIT_COL_PREFIX.length);
/**
 * Re-boxing row read — the row-expression half of the companion scheme.
 * Compiled row fns read columns off arquero row objects whose boxed cells
 * were split by {@link unboxColumns}; re-attaching the row's companion
 * unit here is what lets the value-level unit policies (`defineFold`'s
 * kernel, the box-aware comparison coercers) see the same boxed cells the
 * engine sees. Rows with no companion — engine-side rows handed to agg /
 * pivot reducers, or plain unboxed columns — pass through untouched,
 * including cells that are ALREADY boxed.
 */
const reboxedRead = (row: Record<string, unknown>, name: string): unknown => {
  const v = row[name];
  if (typeof v !== 'number') {
    return v;
  }
  const unit = row[unitColName(name)] as Unit | null | undefined;
  return unit == null ? v : unitCell(v, unit);
};

/**
 * Lazy re-boxing view over a row: string-keyed property reads route
 * through {@link reboxedRead}, so a `%`-bound row param (and every
 * `(col % …)` / `(:col %)` walk off it) yields boxed cells. Key
 * enumeration hides the synthetic unit companions (they never escape
 * the boundary — not even into "did you mean" diagnostics); the other
 * traps forward to the underlying row.
 */
const reboxedRowView = (row: Record<string, unknown>): Record<string, unknown> =>
  new Proxy(row, {
    get: (target, prop) => (typeof prop === 'string' ? reboxedRead(target, prop) : Reflect.get(target, prop)),
    ownKeys: (target) => Reflect.ownKeys(target).filter((k) => typeof k !== 'string' || !isUnitColName(k)),
  });

/**
 * Split boxed columns into plain-number values + unit companions
 * (identity for columns with no boxed cell). The arquero-entry half of
 * the companion scheme; shared by {@link toTable}, {@link withTable}'s
 * and {@link runtimeJoin}'s identity-indexed table builds.
 */
const unboxColumns = (columns: Columns): Record<string, readonly unknown[]> =>
  Object.fromEntries(
    Object.entries(columns).flatMap(([name, cells]): readonly (readonly [string, readonly unknown[]])[] => {
      if (!cells.some(isUnitCell)) {
        return [[name, cells]];
      }
      return [
        [name, cells.map((c) => (isUnitCell(c) ? c.value : c))],
        [unitColName(name), cells.map((c) => (isUnitCell(c) ? c.unit : null))],
      ];
    }),
  );
/**
 * Convert a materialised engine `Dataset` to an arquero `ColumnTable`.
 * arquero only handles populated columns, so a `LazyDataset` must be
 * materialised before crossing this arquero boundary. Callers narrow via
 * `expectMaterialized` (verbs do so via the dispatch wrapper — verbs
 * themselves never see lazy inputs). Unit-bearing cells are split into
 * plain numbers + `__cog_unit__*` companions ({@link unboxColumns});
 * {@link fromTable} re-boxes on exit.
 */
export const toTable = (ds: MaterializedDataset): ColumnTable => aqTable(unboxColumns(ds.columns));

/**
 * Convert an arquero `ColumnTable` back to a `MaterializedDataset`.
 * Materialises every column to a plain `Cell[]` so the result has no
 * arquero-internal types leaking through. Cells may be `Scalar` (the
 * common case), a nested `Dataset` (the "TS reference" carrier shape),
 * or a re-boxed `UnitCell`: a `__cog_unit__*` companion column
 * (injected by {@link toTable} / {@link unboxColumns}) re-boxes its value
 * column row-wise — unit present and value numeric → boxed cell — and is
 * then stripped, so companions never escape the boundary.
 *
 * This function always produces populated columns and is the canonical materialised
 * exit from the arquero boundary. Verbs (and `withTable`) carry that
 * tightness up the call chain.
 *
 * The caller must supply the result `Schema`. The engine's
 * schema is authoritative — it is *not* inferred by inspecting the
 * arquero table, since arquero's type information is based on the
 * first row's JS runtime types and is poorer than the engine's
 * `ColumnType` system. Callers compute the output schema via the
 * relevant op's `propagateSchema` function (verbs) or via
 * `unknownSchemaFor(t.columnNames())` when no propagator applies —
 * companion names picked up by such a synthesised schema are stripped
 * here alongside their columns.
 *
 * The supplied schema is then run through {@link refineSchema}: any
 * column the propagator left `unknown` (every `unknownSchemaFor`
 * column, every agg/reshape/join output, every `?? unknownCol`
 * fallback) is re-typed from its actual cell values here — the single
 * materialisation seam downstream of every lossy propagator. Strictly
 * fill-only, so an authoritative concrete prediction is untouched and
 * the static type-check (run on the still-`unknown` plan schema) is
 * unaffected; `materialize`'s `schemaRefines` post-condition permits
 * the result being more specific than predicted.
 *
 * `identity` (optional) is the row-aligned identity axis the caller
 * gathered across the arquero permutation (see {@link withTable}'s
 * row-index carry); omitted (and so `undefined`) when the input dataset
 * carried no identity.
 */
export const fromTable = (t: ColumnTable, schema: Schema, identity?: IdentityAxis): MaterializedDataset => {
  const names = t.columnNames();
  const unitsByCol = new Map(
    names.filter(isUnitColName).map((n) => [valueColName(n), [...t.array(n)] as readonly (Unit | null | undefined)[]]),
  );
  const reboxed = (name: string): readonly Cell[] => {
    const raw = [...t.array(name)] as readonly Cell[];
    const units = unitsByCol.get(name);
    if (units === undefined) {
      return raw;
    }
    return raw.map((v, i) => boxNumericCell(v, units[i] ?? undefined));
  };
  const columns: Columns = Object.fromEntries(names.filter((n) => !isUnitColName(n)).map((n) => [n, reboxed(n)]));
  const engineSchema = [...schema.keys()].some(isUnitColName)
    ? new Map([...schema.entries()].filter(([k]) => !isUnitColName(k)))
    : schema;
  return new MaterializedDataset(columns, refineSchema(engineSchema, columns), identity);
};

/**
 * Reserved row-index column injected around an arquero permutation so
 * the engine's identity axis can be regathered after arquero reorders /
 * filters / joins rows. The name is deliberately exotic so no
 * user-projectable column ever collides with it, and it is read by no
 * user row-function (those read real columns by name), so it stays
 * invisible to the user op (SC1). Always injected and stripped strictly
 * around the arquero boundary — never observed by engine consumers.
 */
const ROW_INDEX_COL = '__cog_identity_row_index__';

/**
 * Regather a row-aligned identity axis across an arquero permutation and
 * strip the {@link ROW_INDEX_COL} carrier. `out` is the post-op table
 * that still carries the reserved index; `source` is the pre-op axis.
 * Each output row's identity is `source[itsCarriedIndex]`; a row with no
 * carried index (e.g. a right-only row of a full join — the anchor side
 * was null) yields `undefined`. The SSOT shared by {@link withTable} and
 * {@link runtimeJoin}.
 */
const regatherIdentity = (
  out: ColumnTable,
  source: readonly RowIdentity[],
): { readonly table: ColumnTable; readonly identity: readonly RowIdentity[] } => {
  const survivingIndices = [...out.array(ROW_INDEX_COL)] as readonly (number | undefined)[];
  const identity = survivingIndices.map((i) => (i === undefined ? undefined : source[i]));
  return { table: out.select(aqNot(ROW_INDEX_COL)), identity };
};

/**
 * Verb-helper: round-trip a `MaterializedDataset` through arquero.
 * Every verb's apply converts the threaded Dataset to a `ColumnTable`,
 * applies an arquero op, then converts back. Centralises the arquero
 * boundary crossing in one place.
 *
 * Output schema is synthesised as all-`unknown` from the result
 * table's column names; verbs with a registered `propagateSchema`
 * will replace this with a precise schema downstream
 * (see `runPropagateSchema` in `schema/infer.ts`).
 *
 * Lives here (in the arquero boundary file) so verb files don't
 * transitively import `builtins.ts` — that import-cycle would race
 * verb module registration against `builtins.ts`'s module-level
 * initialisation order.
 *
 * IDENTITY ROW-INDEX CARRY: arquero verbs reorder / filter / join rows,
 * so the engine's row-aligned identity axis cannot survive the round
 * trip on its own. When `ds.identity !== undefined`, inject a reserved
 * unique integer index column `[0..n)` BEFORE `op`, then after `op`
 * regather `ds.identity[survivingIndex]` per output row and strip the
 * index ({@link regatherIdentity}). The reserved name is guaranteed
 * unique (no user column collides) and is read by no user row-function,
 * so the user op never observes it. When `ds.identity === undefined`,
 * skip the carry entirely — pure pass-through to the prior behaviour.
 */
export const withTable = (ds: MaterializedDataset, op: (t: ColumnTable) => ColumnTable): MaterializedDataset => {
  if (ds.identity === undefined) {
    const out = op(toTable(ds));
    return fromTable(out, unknownSchemaFor(out.columnNames()));
  }
  const indexed = aqTable({ ...unboxColumns(ds.columns), [ROW_INDEX_COL]: ds.identity.map((_, i) => i) });
  const carried = regatherIdentity(op(indexed), ds.identity);
  return fromTable(carried.table, unknownSchemaFor(carried.table.columnNames()), carried.identity);
};

/**
 * Per-`JoinType` arquero dispatch. Each entry calls the appropriate
 * `ColumnTable.join_*` method with `[leftKeys, rightKeys]` shape that
 * arquero accepts directly. Output schema is synthesised by the verb's
 * `propagateSchema`; this adapter only owns the join mechanics.
 */
const JOIN_FNS: Record<
  JoinType,
  (l: ColumnTable, r: ColumnTable, on: [readonly string[], readonly string[]]) => ColumnTable
> = {
  inner: (l, r, on) => l.join(r, on as [string[], string[]]),
  left: (l, r, on) => l.join_left(r, on as [string[], string[]]),
  right: (l, r, on) => l.join_right(r, on as [string[], string[]]),
  full: (l, r, on) => l.join_full(r, on as [string[], string[]]),
  // Keyless cartesian product — `on` is empty and ignored. A single-row
  // right side broadcasts onto every left row.
  cross: (l, r) => l.cross(r),
};

/**
 * Cross the arquero boundary for joins. The caller (`join` op)
 * supplies the two materialised datasets + the join keys + the
 * join type; this helper does the arquero round-trip and hands
 * back a `MaterializedDataset` whose schema is recovered from
 * actual cells by `fromTable`'s value-based `refineSchema`.
 *
 * IDENTITY: the LEFT side is the join anchor. Its row identity carries
 * through to the output via the {@link ROW_INDEX_COL} carry (injected on
 * the left table only — the right side carries no index, so there is no
 * suffix collision). An inner / left / right join's output row that has
 * an anchor (left) row keeps that row's identity; a full-join row with
 * no anchor row (right-only) carries the index as `undefined`, so
 * {@link regatherIdentity} yields `undefined` — never a fabricated
 * space. Skipped entirely when the anchor side has no identity axis.
 */
export const runtimeJoin = (
  left: MaterializedDataset,
  right: MaterializedDataset,
  on: [readonly string[], readonly string[]],
  type: JoinType,
): MaterializedDataset => {
  // Arquero suffixes colliding column names (`_1`/`_2`), and a colliding
  // boxed column's unit companion must suffix in lockstep — a companion
  // present on only one side keeps its bare name post-join and orphans,
  // dropping that column's per-row units. Null-fill the missing companion so
  // companions collide exactly when their base columns do.
  const fillCompanions = (
    target: Record<string, readonly unknown[]>,
    other: Record<string, readonly unknown[]>,
  ): Record<string, readonly unknown[]> => {
    const rows = Object.values(target)[0]?.length ?? 0;
    const missing = Object.keys(target)
      .filter((name) => other[name] !== undefined)
      .map(unitColName)
      .filter((companion) => other[companion] !== undefined && target[companion] === undefined);
    return { ...target, ...Object.fromEntries(missing.map((c) => [c, Array.from({ length: rows }, () => null)])) };
  };
  const leftRaw = unboxColumns(left.columns);
  const rightRaw = unboxColumns(right.columns);
  const rT = aqTable(fillCompanions(rightRaw, leftRaw));
  const leftCols = fillCompanions(leftRaw, rightRaw);
  if (left.identity === undefined) {
    const out = JOIN_FNS[type](aqTable(leftCols), rT, on);
    return fromTable(out, unknownSchemaFor(out.columnNames()));
  }
  const indexedLeft = aqTable({ ...leftCols, [ROW_INDEX_COL]: left.identity.map((_, i) => i) });
  const carried = regatherIdentity(JOIN_FNS[type](indexedLeft, rT, on), left.identity);
  return fromTable(carried.table, unknownSchemaFor(carried.table.columnNames()), carried.identity);
};
/**
 * Rename columns across the arquero boundary, carrying each renamed
 * column's unit companion along so per-row units survive the relabel.
 * The single sanctioned rename entry point — a bare `t.rename` inside
 * `withTable` would orphan the companion (`fromTable`'s re-box pass
 * keys companions by the VALUE column's name, so the units would be
 * silently dropped and the orphan stripped).
 */
export const runtimeRename = (
  ds: MaterializedDataset,
  mapping: Readonly<Record<string, string>>,
): MaterializedDataset =>
  withTable(ds, (t) =>
    t.rename(
      Object.fromEntries(
        Object.entries(mapping).flatMap(([from, to]) =>
          t.columnNames().includes(unitColName(from))
            ? [[from, to] as const, [unitColName(from), unitColName(to)] as const]
            : [[from, to] as const],
        ),
      ),
    ),
  );

/**
 * Per-lambda-call parameter substitution map. When `compileExpr`
 * inlines a lambda invocation in expression context, it compiles each
 * argument into a `RowFn`, then compiles the lambda body in a context
 * where the param names map directly to those compiled arg fns. This
 * sidesteps the env-binding mechanism (which carries `Value`s, not
 * row functions) and keeps lambda invocation a pure per-row dispatch.
 */
type LambdaSubst = ReadonlyMap<string, RowFn>;

const EMPTY_SUBST: LambdaSubst = new Map();

/**
 * Compile an AST node to a per-row function. Walks:
 * - Literals (number/string/boolean/null/Date/Dataset) → constant fn.
 * - Vectors → array of compiled fns; row produces an array.
 * - Symbol refs: lambda param substitution wins; then env (`let` /
 *   `def` lexical binding); then row column fallthrough (`(row) =>
 *   row[name]`). A `Dataset`-typed env binding or a row cell
 *   containing a `Dataset` throws `NestedDatasetCellError`.
 * - Op calls: a head resolving in env to a `LambdaValue` triggers
 *   inline lambda expansion (compile args, then compile body in the
 *   captured closure env with a fresh subst). Otherwise dispatch to
 *   the op's own `compileExpr` method via `opMeta`. Ops without a
 *   `compileExpr` method throw `NotAnExpressionOpError`.
 *
 * Async because nested op `compileExpr` hooks may themselves be async
 * (e.g. when they recursively compile child expressions). The async
 * overhead is paid once per `filter` / `derive` call (when the verb's
 * apply runs `await ctx.compile(predicate, …)`); the returned `RowFn`
 * is sync per row.
 *
 * `spans` and `path` flow through so any thrown error carries the source
 * position of the offending node.
 */
export const compileExpr = async (
  node: AstInput,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
  env: Env,
  subst: LambdaSubst = EMPTY_SUBST,
): Promise<RowFn> => {
  const constant = (shape: { readonly value: Value }): RowFn => {
    const v = shape.value;
    return () => v;
  };
  return foldAst<Promise<RowFn>>(node, {
    embedded: async (shape) => constant(shape),
    vector: async (shape) => {
      const compiled = await Promise.all(
        shape.elements.map((el, i) => compileExpr(el, ops, identities, spans, elemPath(path, i), env, subst)),
      );
      return (row) => compiled.map((f) => f(row));
    },
    keyword: async (shape) => {
      const keyword = new Keyword(shape.name);
      return () => keyword;
    },
    map: async (shape) => {
      const compiledEntries = await Promise.all(
        shape.entries.map(async (entry, i) => {
          const f = await compileExpr(entry.v, ops, identities, spans, mapValuePath(path, entry.k, i), env, subst);
          return [entry.k, f] as const;
        }),
      );
      return (row) => new MapValue(new Map(compiledEntries.map(([k, f]) => [k, f(row) as Value] as const)));
    },
    symbolRef: async (shape) => {
      const { sym } = shape;
      const pos = nodePos(spans, path);
      // (1) Lambda param substitution takes priority — these are the
      // only names guaranteed to live "in front of" everything else
      // for the duration of a lambda body's compilation.
      const paramFn = subst.get(sym);
      if (paramFn !== undefined) {
        return paramFn;
      }
      // (2) Env lookup. A name bound in scope (via `let` / `def` /
      // lambda closure) shadows any row column of the same name.
      const envValue = lookup(env, sym);
      if (envValue !== undefined) {
        return constantRowFn(envValue, sym, pos);
      }
      // (3) Row column fallthrough — names not in scope resolve to
      // the current row's column of that name, re-boxed with the row's
      // companion unit when one rides along ({@link reboxedRead}).
      return (row) => {
        const v = reboxedRead(row, sym);
        if (v instanceof Dataset) {
          throw new NestedDatasetCellError(sym, pos);
        }
        return v;
      };
    },
    opCall: async (shape) => {
      const { call } = shape;
      const callPos = opCallPos(spans, path);
      // (1') Lambda inline expansion: the head resolves in env to a
      // `LambdaValue`. Compile args in the caller's env + subst, then
      // compile the body in the lambda's CLOSURE env (closure
      // semantics) with a fresh subst mapping the lambda's params
      // to the compiled arg fns.
      const headValue = lookup(env, call.name);
      if (headValue !== undefined) {
        if (isLambda(headValue)) {
          return compileLambdaCall(headValue, call, ops, identities, spans, path, env, subst);
        }
        // The head is bound but not callable. `checkBindable` rejects
        // op-name shadowing at bind time, so this is a `(myValue arg)`
        // call where `myValue` is bound to a non-lambda Value. Surface
        // a precise diagnostic at the head position rather than
        // falling through to a misleading `UnknownOpError`.
        throw new TypeMismatchError(describeValueKind(headValue), 'a callable lambda', `(${call.name} …)`, callPos);
      }
      // (2') Standard op dispatch.
      const { meta } = resolveOpCall(call.name, ops, opMeta, callPos, 'op', UnknownOpError);
      // Gate on the hook's presence, not `evaluation`: a special form
      // may opt into expression context (e.g. `(col …)` needs raw AST
      // labels yet must compile inside `derive` / `filter`).
      const compileExprHook = meta.expr?.compileExpr;
      if (compileExprHook === undefined) {
        throw new NotAnExpressionOpError(call.name, callPos);
      }
      const compile: CompileFn = (childNode, childPath) =>
        compileExpr(childNode as AstInput, ops, identities, spans, childPath, env, subst);
      // The stored hook's return is `unknown` to short-circuit Zod's
      // `$replace` recursion through `Promise<RowFn>`; the author
      // annotates the arrow with `Promise<RowFn>` so the runtime
      // contract is sound. See `ExprCapability.compileExpr` for context.
      return compileExprHook(call, compile, path) as Promise<RowFn>;
    },
  });
};

/**
 * Inline a `LambdaValue` invocation. The arg expressions are compiled
 * in the CALLER's env + subst (where they're written); the lambda
 * body is compiled in the LAMBDA's `closureEnv` (where its free
 * variables resolve — classic closure semantics) with a fresh subst
 * binding the param names to the compiled arg fns.
 */
const compileLambdaCall = async (
  lambda: LambdaValue,
  call: { readonly name: string; readonly params?: readonly AstInput[] },
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
  callerEnv: Env,
  callerSubst: LambdaSubst,
): Promise<RowFn> => {
  const args = call.params ?? [];
  if (args.length !== lambda.params.length) {
    throw new LambdaCallArityError(lambda.params.length, args.length, opCallPos(spans, path));
  }
  const argFns = await Promise.all(
    args.map((arg, i) => compileExpr(arg, ops, identities, spans, paramPath(path, i), callerEnv, callerSubst)),
  );
  const innerSubst: LambdaSubst = new Map(lambda.params.map((p, i) => [p, argFns[i] as RowFn]));
  return compileExpr(lambda.body, ops, identities, spans, path, lambda.closureEnv, innerSubst);
};
/**
 * Compile an arbitrary expression to a per-row function with each
 * named param bound to a row-identity `RowFn` (the row map itself,
 * behind the {@link reboxedRowView} so column reads off `%` yield
 * unit-boxed cells).
 * Shared kernel for `filter` (lambda body), `derive` (each map-entry
 * value of a `#({:col expr})` projector), and `sort` (computed-key
 * lambda body). The body's `(:col %)` references resolve through
 * `col`'s `compileExpr` path (which ignores the leading binding
 * prefix), and a bare `%` resolves to the row itself.
 */
export const compileExprAsRowFn = async (
  expr: AstInput,
  paramNames: readonly string[],
  closureEnv: Env,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
): Promise<RowFn> => {
  const rowIdentity: RowFn = (row) => reboxedRowView(row);
  const subst: LambdaSubst = new Map(paramNames.map((p) => [p, rowIdentity]));
  return compileExpr(expr, ops, identities, spans, path, closureEnv, subst);
};

/**
 * Arity gate for verb-invoked projector lambdas. SSOT for "this
 * lambda's parameter list is the wrong shape for the row projection
 * it's about to perform" — both {@link compileProjectorLambda} (the
 * full kernel) and derive's lambda+map form (which compiles each
 * map-entry body via {@link compileExprAsRowFn}, not the kernel) route
 * through this so the {@link ProjectorArityError} recovery hint is
 * unmissable.
 */
export const requireProjectorArity = (lambda: LambdaValue, expectedArity: number, pos: SourcePos): void => {
  if (lambda.params.length !== expectedArity) {
    throw new ProjectorArityError(expectedArity, lambda.params.length, pos);
  }
};
/**
 * Compile a `LambdaValue` as a per-row projector function, with the
 * arity gate enforced at the kernel via {@link requireProjectorArity}.
 * Any verb that invokes a user-supplied lambda per-row routes through
 * here so the {@link ProjectorArityError} recovery hint is unmissable.
 *
 * Callers: `filter` (predicate IS the projector body), `sort` (each
 * computed-key lambda body), `derive`'s kwarg form (each `:col fn` is
 * one projector). `derive`'s lambda+map form calls
 * {@link requireProjectorArity} directly because each map entry
 * compiles to its own row-fn — there is no single lambda body to share
 * with this kernel.
 */
export const compileProjectorLambda = async (
  lambda: LambdaValue,
  expectedArity: number,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
): Promise<RowFn> => {
  requireProjectorArity(lambda, expectedArity, opCallPos(spans, path));
  return compileExprAsRowFn(lambda.body, lambda.params, lambda.closureEnv, ops, identities, spans, path);
};

/**
 * Build a constant `RowFn` for an env-bound symbol used in scalar
 * position. Datasets and lambdas are not legal scalar values — a
 * Dataset env binding referenced scalar-ly throws
 * `NestedDatasetCellError` (matching the row-cell case), and a lambda
 * binding in scalar position throws `ShapeError` ("can't use as
 * scalar"). All other Value kinds — Scalar, vector, ColumnRef,
 * DescSpec, DashboardSpec, Schema — bake in as constants.
 */
const constantRowFn = (value: Value, sym: string, pos: ReturnType<typeof nodePos>): RowFn => {
  if (value instanceof Dataset) {
    throw new NestedDatasetCellError(sym, pos);
  }
  if (isLambda(value)) {
    throw new ShapeError(`'${sym}' is a lambda and cannot be used as a scalar value`, pos);
  }
  return () => value;
};
/**
 * Compiled form of an `AggSpec` — a pure JS reducer over a group's
 * rows. We deliberately do NOT use arquero's `rollup` op-namespace
 * here, even though sum/mean/min/max/count have arquero equivalents:
 * keeping the reducers uniformly manual makes the apply of every agg
 * consumer (`aggregate` / `pivot` / `time-bucket :agg`) trivially
 * simple (one code path) and side-steps the arquero-rollup type
 * machinery for expression objects (which Zod's `$replace` doesn't
 * play well with).
 *
 * The performance cost is acceptable for chat-scale datasets (≤10k
 * rows typical); if a future workflow needs faster aggregation, the
 * op can swap individual reducers to use arquero's `op.*` directly
 * — but the apply stays clean meanwhile.
 */
export type AggReducer = (rows: readonly Record<string, unknown>[]) => unknown;
// `first`/`last` mean the first/last *present* value: null/undefined
// cells are skipped so a null boundary row (a gap-filled time bucket,
// a missing CDF sample) doesn't poison the aggregate. Defaults to null
// when no present value exists.
const isPresentCell = (v: unknown): boolean => v !== null && v !== undefined;

// Filters out null/undefined AND empty/blank strings (note
// `Number('') === 0`, which would otherwise inject phantom zeros from
// missing CDF samples), then coerces via the box-aware `cellNumber` (a
// unit-bearing cell contributes its bare number); NaN is dropped so
// "no valid input" surfaces as an empty array (→ kernel returns null).
const validNumbersFrom = (values: readonly unknown[]): readonly number[] =>
  values
    .filter((v) => isPresentCell(v) && !(typeof v === 'string' && v.trim() === ''))
    .map(cellNumber)
    .filter((n) => !Number.isNaN(n));

/**
 * Per-op value-array kernels — the ONE place scalar-reducer null/blank
 * handling lives, and the SSOT for which scalar reducers exist (see
 * {@link ArqueroAggOpName}). Operate on a raw `unknown[]` of per-row values
 * without knowing the source: `col-agg` feeds column cells, `expr-agg` feeds
 * compiled `RowFn` outputs. `SPEC_KIND_REDUCERS` arms compose this kernel with
 * the appropriate source extractor inline — no parallel mirror.
 *
 * `stddev`/`variance` are population statistics (÷n), matching
 * `numeric-stats.computeNumericStats`; `range` is max − min.
 */
export const VALUE_KERNELS = {
  sum: (values: readonly unknown[]) => validNumbersFrom(values).reduce((s, n) => s + n, 0),
  mean: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : ns.reduce((s, n) => s + n, 0) / ns.length;
  },
  min: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : Math.min(...ns);
  },
  max: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : Math.max(...ns);
  },
  first: (values: readonly unknown[]) => values.find(isPresentCell) ?? null,
  last: (values: readonly unknown[]) => {
    for (let i = values.length - 1; i >= 0; i -= 1) {
      const v = values[i];
      if (isPresentCell(v)) {
        return v;
      }
    }
    return null;
  },
  count: (values: readonly unknown[]) => values.filter(isPresentCell).length,
  stddev: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : standardDeviation([...ns]);
  },
  variance: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : variance([...ns]);
  },
  median: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : median([...ns]);
  },
  range: (values: readonly unknown[]) => {
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : Math.max(...ns) - Math.min(...ns);
  },
} satisfies Record<string, (values: readonly unknown[]) => unknown>;
/** Stamp a numeric aggregate with its group's unified unit; non-numeric results pass through. */
const withUnifiedUnit = (result: unknown, values: readonly unknown[]): unknown => {
  if (typeof result !== 'number') {
    return result;
  }
  const unit = unifiedUnitOfCells(values);
  return unit === undefined ? result : unitCell(result, unit);
};

/**
 * Run a scalar kernel over a group's values, boxing a numeric result with
 * the group's unified unit. `count` is dimensionless by construction and
 * stays plain. NOTE: `stddev`/`variance` keep the INPUT unit — parity with
 * the type-level stamping this replaces (variance's squared dimension is
 * deliberately not modelled).
 */
const runKernel = (op: ArqueroAggOpName, values: readonly unknown[]): unknown =>
  op === 'count' ? VALUE_KERNELS[op](values) : withUnifiedUnit(VALUE_KERNELS[op](values), values);

/**
 * Scalar-reducer name union — derived from {@link VALUE_KERNELS} so the kernel
 * map is the single source of truth: registering a kernel above extends this
 * union automatically. Re-exported from `op-meta.ts` for a single import surface.
 */
export type ArqueroAggOpName = keyof typeof VALUE_KERNELS;

/**
 * Per-`AggSpec.kind` dispatch table. Each entry takes the spec and
 * returns the compiled reducer. Lookup-map dispatch is the SSOT for
 * spec-kind dispatch — keeps `compileAggSpec` a one-line lookup.
 *
 * Every scalar-reducer op (sum/mean/min/max/first/last) lands on
 * `expr-agg`; `col-pick` and `col-array` route the same kernel set
 * over a direct column lookup. ONE code path through `VALUE_KERNELS`
 * (via {@link runKernel}, which also stamps the group's unified unit;
 * `col-pick` first/last MOVE a cell, so a boxed cell passes through
 * intact without re-derivation).
 */
const SPEC_KIND_REDUCERS: {
  readonly [KindType in AggSpec['kind']]: (spec: Extract<AggSpec, { kind: KindType }>) => AggReducer;
} = {
  'expr-agg': (spec) => (rows) => runKernel(spec.op, rows.map(spec.rowFn)),
  'row-count': () => (rows) => rows.length,
  'col-array': (spec) => (rows) => rows.map((r) => r[spec.col]),
  'col-pick': (spec) => (rows) => VALUE_KERNELS[spec.pick](rows.map((r) => r[spec.col])),
  'quantile-agg': (spec) => (rows) => {
    const values = rows.map(spec.rowFn);
    const ns = validNumbersFrom(values);
    return ns.length === 0 ? null : withUnifiedUnit(quantile([...ns], spec.p), values);
  },
  'reduce-rows': (spec) => spec.reduce,
};

/**
 * Translate an engine-side `AggSpec` into a pure-JS `AggReducer`.
 * Every variant maps to a reducer that consumes a group's row slice
 * (already partitioned by the verb's groupby logic) and returns a
 * single value.
 *
 * Null / NaN handling: numeric reducers (sum/mean/min/max) filter
 * out null, NaN, and empty/blank-string inputs (`Number('') === 0`
 * would otherwise inject phantom zeros). An all-invalid input set
 * returns `null` for mean/min/max and `0` for sum (matches
 * arithmetic intuition). The `first`/`last` pick reducers skip
 * null/undefined cells and return the first/last *present* value
 * (so a null boundary row — a gap-filled time bucket or a missing
 * CDF sample — doesn't poison the aggregate); falsy-but-present
 * values like `0`/`''` are preserved.
 */
export const compileAggSpec = (spec: AggSpec): AggReducer =>
  (SPEC_KIND_REDUCERS[spec.kind] as (s: AggSpec) => AggReducer)(spec);
/**
 * Structural sort-key shape — `{column, direction}` records produced
 * by the `sort` verb (and any other consumer that needs arquero
 * orderby args). Typed structurally so this file does not depend on
 * the verb's internal `SortKey` alias; the verb supplies records
 * conforming to this shape.
 *
 * `dateAware` is set by the verb when the column is a `timestamp` /
 * `date` type: arquero's default comparator sorts string cells
 * lexically, which mis-orders mixed-offset / non-zero-padded / epoch
 * date representations. A `dateAware` key is ordered by parsed instant
 * instead, so the result is always chronological.
 */
export type SortKey = {
  readonly column: string;
  readonly direction: SortDirection;
  readonly dateAware?: boolean;
};

/**
 * Translate engine-side `SortKey[]` into the variadic args arquero's
 * `table.orderby(…)` accepts. Asc keys pass through as bare column
 * names; desc keys wrap with arquero's `desc(name)` helper. A
 * `dateAware` key is ordered by parsed instant (an escaped row
 * expression over `toDateValue`) instead of arquero's lexical
 * default, so `timestamp`/`date` columns sort chronologically
 * regardless of cell representation (ISO string, epoch, `Date`).
 * Keeps the arquero boundary intact — verbs import this helper instead
 * of arquero directly.
 */
export const compileSortKeys = (keys: readonly SortKey[]): readonly (string | ReturnType<typeof aqDesc>)[] =>
  keys.map((k) => {
    if (!k.dateAware) {
      return k.direction === 'asc' ? k.column : aqDesc(k.column);
    }
    const col = k.column;
    const instant = aqEscape((d: Record<string, unknown>) => {
      const dt = toDateValue(d[col]);
      return dt === null ? null : dt.getTime();
    });
    return k.direction === 'asc' ? instant : aqDesc(instant);
  });
/**
 * Row count of a `MaterializedDataset` without materialising any rows:
 * the length of its first column (all columns are equal-length by the
 * `MaterializedDataset` constructor invariant). Zero columns → 0 rows.
 *
 * Lives beside `materializedToRows` because it is the same kind of
 * column-oriented dataset-shape query; verbs that assemble output
 * column-wise (e.g. `unnest`) need the row count without paying the
 * row-object allocation `materializedToRows` would incur.
 */
export const materializedRowCount = (ds: MaterializedDataset): number =>
  (Object.values(ds.columns)[0] as readonly Cell[] | undefined)?.length ?? 0;
/**
 * Convert a `MaterializedDataset` to a row-oriented array of records.
 * Cell shape is preserved verbatim (`Cell = Scalar | Dataset`); the
 * function is pure JS, used by ops that need row-iteration
 * (`aggregate` / `pivot` groupby, `time-bucket` / `fold` row scans).
 *
 * `Record<string, unknown>` return type so manual reducers
 * (`AggReducer`) can consume the rows without each consumer threading
 * its own `Cell` cast. The values are still `Cell` at runtime; the
 * looser type is for the reducer's convenience.
 *
 * Lives here (in the arquero boundary file) alongside `toTable` /
 * `fromTable` because it's structurally the same kind of helper —
 * a column-oriented ↔ row-oriented conversion — and the file already
 * owns dataset-row plumbing.
 */
export const materializedToRows = (ds: MaterializedDataset): readonly Record<string, unknown>[] => {
  const cols = Object.keys(ds.columns);
  if (cols.length === 0) {
    return [];
  }
  const firstCol = ds.columns[cols[0] as string];
  const n = firstCol?.length ?? 0;
  const rows: Record<string, unknown>[] = [];
  for (let i = 0; i < n; i++) {
    const row: Record<string, unknown> = {};
    for (const c of cols) {
      row[c] = ds.columns[c]?.[i];
    }
    rows.push(row);
  }
  return rows;
};

/**
 * Sentinel for "no fill source seen yet" — distinct from a real `null`
 * cell (a `null` is a gap to be filled, never a carry source).
 */
const NO_CARRY = Symbol('no-carry');

/** A cell is a fill *gap* iff it is `null`/`undefined`. Only gaps are
 * ever written; only a non-gap value is ever a carry source. */
const isFillGap = (c: Cell): boolean => c === null || c === undefined;

/**
 * Null-skipping carry over an already-ordered `Cell[]` — the kernel
 * behind `forward-fill`/`backward-fill` AND `time-bucket :fill` (SSOT).
 *
 * Semantics (locked with the stakeholder):
 *   1. Only gap cells (`null`/`undefined`) are ever written.
 *   2. Only a real value is ever a carry source — a gap never
 *      propagates as if it were a value.
 *   3. Observations are never overwritten.
 *   4. No source in range ⇒ the gap stays `null` (leading gaps for
 *      ffill / trailing gaps for bfill).
 *   5. `limit` caps how many consecutive gaps one source may fill;
 *      gaps beyond the cap stay `null`. It never relaxes 1–2.
 *
 * Pure: returns a new array, input untouched. No arquero — arquero's
 * native window ops do not skip nulls, so LOCF/NOCB is hand-rolled
 * here (the one documented gap in arquero's window surface).
 */
export const fillColumnArray = (values: readonly Cell[], dir: FillDirection, limit?: number): Cell[] => {
  const out = values.slice();
  const n = out.length;
  const step = dir === 'ffill' ? 1 : -1;
  let i = dir === 'ffill' ? 0 : n - 1;
  let carry: Cell | typeof NO_CARRY = NO_CARRY;
  let run = 0;
  for (let seen = 0; seen < n; seen++, i += step) {
    const cur = out[i] as Cell;
    if (!isFillGap(cur)) {
      carry = cur;
      run = 0;
      continue;
    }
    if (carry !== NO_CARRY && (limit === undefined || run < limit)) {
      out[i] = carry as Cell;
      run += 1;
    }
  }
  return out;
};

/**
 * Order descriptor for a window frame. `dateAware` parses cells to an
 * instant before comparing (mirrors `sort`'s temporal handling) so
 * `timestamp`/`date` columns frame chronologically regardless of cell
 * representation. `undefined` order ⇒ input row order is the frame.
 */
export type WindowOrder = { readonly column: string; readonly dateAware: boolean };

/** Shared shape of a column-producing window op — derives `out` from `col`
 * within the frame. One type parameter per discriminant keeps each scan arm a
 * single literal `kind` (so `Extract<WindowSpec, {kind:K}>` resolves). */
type ColProducingSpec<KindType extends string> = {
  readonly kind: KindType;
  readonly col: string;
  readonly out: string;
  readonly order?: WindowOrder;
  readonly partitionBy: readonly string[];
};

/** A rolling op over a sized window. */
type RollingSpec<KindType extends string> = ColProducingSpec<KindType> & { readonly window: WindowExtent };

/** A per-time op carrying a ms-per-unit scale factor. */
type ScaleSpec<KindType extends string> = ColProducingSpec<KindType> & { readonly scale: number };

/** A window operation, fully resolved by the verb before it crosses
 * the arquero boundary. `partitionBy` empty ⇒ one global frame. */
export type WindowSpec =
  | {
      readonly kind: 'ffill' | 'bfill';
      readonly col: string;
      readonly order?: WindowOrder;
      readonly partitionBy: readonly string[];
      readonly limit?: number;
    }
  | {
      readonly kind: 'first-value' | 'last-value';
      readonly col: string;
      readonly out: string;
      readonly order?: WindowOrder;
      readonly partitionBy: readonly string[];
    }
  | {
      readonly kind: 'lag' | 'lead';
      readonly col: string;
      readonly out: string;
      readonly offset: number;
      readonly default: Cell;
      readonly order?: WindowOrder;
      readonly partitionBy: readonly string[];
    }
  // Column-producing "scan" ops: order-dependent, derive one column from `col`.
  // One arm per kind (single-literal `kind`) so `Extract<WindowSpec, {kind:K}>`
  // resolves to a concrete arm for the scan dispatch tables.
  | RollingSpec<'rolling-mean'>
  | RollingSpec<'rolling-sum'>
  | RollingSpec<'rolling-min'>
  | RollingSpec<'rolling-max'>
  | RollingSpec<'rolling-stddev'>
  // `scale` = ms-per-unit on a temporal axis (slope → per unit time), else 1.
  | (RollingSpec<'rolling-slope'> & { readonly scale: number })
  | (ColProducingSpec<'ewma'> & { readonly decay: EwmaDecay })
  | ColProducingSpec<'cumsum'>
  | ColProducingSpec<'cummax'>
  | ColProducingSpec<'cummin'>
  | ColProducingSpec<'delta'>
  | ColProducingSpec<'pct-change'>
  // `scale` = ms-per-rate-unit: integral divides by it (→ volume), rate
  // multiplies by it (→ value-per-unit-time).
  | ScaleSpec<'cum-integral'>
  | ScaleSpec<'rate-of-change'>;

/** Frame-order comparable for a cell: parsed instant when `dateAware`
 * (shares the `toDateValue` SSOT with `compileSortKeys`), else the bare
 * number (plain or unit-boxed, via `numericScalarOf`) or the cell's
 * string form. Stable tie-break is applied by the caller. */
const frameKey = (cell: Cell, dateAware: boolean): number | string => {
  if (dateAware) {
    const dt = toDateValue(cell);
    return dt === null ? Number.NEGATIVE_INFINITY : dt.getTime();
  }
  return numericScalarOf(cell) ?? String(cell ?? '');
};

/**
 * Order each partition by the frame's `:order-by`, preserving original
 * row position as the stable tie-break. Identity when no order is set.
 * Extracted to keep {@link framedPartitions} at a single level of
 * nesting.
 */
const orderPartitions = (
  parts: readonly (readonly number[])[],
  spec: WindowSpec,
  ds: MaterializedDataset,
): readonly (readonly number[])[] => {
  const order = spec.order;
  if (order === undefined) {
    return parts;
  }
  const ordCol = ds.columns[order.column] ?? [];
  return parts.map((part) =>
    part
      .map((i, idx) => ({ i, idx, k: frameKey(ordCol[i] as Cell, order.dateAware) }))
      .sort((a, b) => {
        if (a.k < b.k) {
          return -1;
        }
        if (a.k > b.k) {
          return 1;
        }
        return a.idx - b.idx;
      })
      .map((e) => e.i),
  );
};

/**
 * Partition row indices, each partition returned in frame order.
 * Preserves the original index so results scatter back to input row
 * positions — window verbs never reorder their output (that is `sort`'s
 * job; `:order-by` defines only the frame).
 *
 * Thin adapter over {@link partitionByKeys} keyed by the partition-by
 * column cells at each row index. Empty `partitionBy` collapses to a
 * single partition over all indices.
 */
const framedPartitions = (ds: MaterializedDataset, spec: WindowSpec): readonly (readonly number[])[] => {
  const n = materializedRowCount(ds);
  const indices = Array.from({ length: n }, (_, i) => i);
  if (spec.partitionBy.length === 0) {
    return orderPartitions([indices], spec, ds);
  }
  const cols = spec.partitionBy.map((c) => (ds.columns[c] ?? []) as readonly Cell[]);
  const extractors = cols.map((col) => (i: number) => col[i] ?? null);
  const partitions = partitionByKeys(indices, extractors).map(({ items }) => items);
  return orderPartitions(partitions, spec, ds);
};

/** The order-dependent, column-producing "scan" window ops — computed in JS
 * rather than via arquero's native window machinery. Every `WindowSpec` kind
 * except the fill/shift/value kinds handled directly in {@link applyWindow}. */
type ScanWindowSpec = Extract<
  WindowSpec,
  {
    kind:
      | 'rolling-mean'
      | 'rolling-sum'
      | 'rolling-min'
      | 'rolling-max'
      | 'rolling-stddev'
      | 'rolling-slope'
      | 'ewma'
      | 'cumsum'
      | 'cummax'
      | 'cummin'
      | 'cum-integral'
      | 'delta'
      | 'pct-change'
      | 'rate-of-change';
  }
>;

/** Frame-ordered x-axis (the `:order-by` instant/number) for one partition;
 * falls back to ordered position when no frame order is set. */
const scanXs = (spec: ScanWindowSpec, part: readonly number[], timeCol: readonly Cell[] | undefined): number[] =>
  part.map((i, k) => {
    if (timeCol === undefined || spec.order === undefined) {
      return k;
    }
    const key = frameKey(timeCol[i] as Cell, spec.order.dateAware);
    return typeof key === 'number' ? key : k;
  });

/** Frame-ordered y values for one partition; non-numeric cells become NaN gaps
 * (mirrors {@link validNumbersFrom}, but position-preserving). */
const scanYs = (part: readonly number[], src: readonly Cell[]): number[] =>
  part.map((i) => {
    const v = src[i];
    if (!isPresentCell(v) || (typeof v === 'string' && v.trim() === '')) {
      return Number.NaN;
    }
    const n = cellNumber(v);
    return Number.isNaN(n) ? Number.NaN : n;
  });

/** Per-scan-kind kernel dispatch. Each entry receives its narrowed spec and the
 * frame-ordered (xs, ys); the mapped type keeps it exhaustive. Mirrors
 * {@link SPEC_KIND_REDUCERS}. */
const SCAN_KERNELS: {
  readonly [KindType in ScanWindowSpec['kind']]: (
    spec: Extract<ScanWindowSpec, { kind: KindType }>,
    xs: readonly number[],
    ys: readonly number[],
  ) => (number | null)[];
} = {
  'rolling-mean': (spec, xs, ys) => rollingWindow(xs, ys, spec.window, 'mean'),
  'rolling-sum': (spec, xs, ys) => rollingWindow(xs, ys, spec.window, 'sum'),
  'rolling-min': (spec, xs, ys) => rollingWindow(xs, ys, spec.window, 'min'),
  'rolling-max': (spec, xs, ys) => rollingWindow(xs, ys, spec.window, 'max'),
  'rolling-stddev': (spec, xs, ys) => rollingWindow(xs, ys, spec.window, 'stddev'),
  'rolling-slope': (spec, xs, ys) => rollingSlope(xs, ys, spec.window, spec.scale),
  ewma: (spec, xs, ys) => exponentialMovingAverage(xs, ys, spec.decay),
  cumsum: (_spec, _xs, ys) => cumulative(ys, 'sum'),
  cummax: (_spec, _xs, ys) => cumulative(ys, 'max'),
  cummin: (_spec, _xs, ys) => cumulative(ys, 'min'),
  'cum-integral': (spec, xs, ys) => trapezoidIntegral(xs, ys, spec.scale),
  delta: (_spec, _xs, ys) => discreteDifference(ys, 'delta'),
  'pct-change': (_spec, _xs, ys) => discreteDifference(ys, 'pct'),
  'rate-of-change': (spec, xs, ys) => rateOfChange(xs, ys, spec.scale),
};

/** Dispatch a scan spec to its pure {@link series-window} kernel. */
const computeScan = (spec: ScanWindowSpec, xs: readonly number[], ys: readonly number[]): (number | null)[] =>
  (SCAN_KERNELS[spec.kind] as (s: ScanWindowSpec, xs: readonly number[], ys: readonly number[]) => (number | null)[])(
    spec,
    xs,
    ys,
  );
/** Scatter per-partition results back into an `n`-length column indexed by
 * original row position. `compute(part)` returns one cell per index-position in
 * `part`; the cell at position `k` lands at source row `part[k]`. Window verbs
 * never reorder output, so each result returns to its source row. Pure: builds a
 * fresh array, mutates nothing. Indices no partition covered stay `null`. */
const scatterParts = (
  n: number,
  parts: readonly (readonly number[])[],
  compute: (part: readonly number[]) => readonly Cell[],
): Cell[] => {
  const byIndex = new Map<number, Cell>(
    parts.flatMap((part) => {
      const vals = compute(part);
      return part.map((rowIndex, k) => [rowIndex, vals[k] as Cell] as const);
    }),
  );
  return Array.from({ length: n }, (_, i) => byIndex.get(i) ?? null);
};

/** Per-`WindowSpec.kind` applier. Each entry owns one window family and returns
 * the result dataset; the mapped type keeps the table exhaustive over every
 * `WindowSpec` kind. Mirrors {@link SCAN_KERNELS}/{@link JOIN_FNS}. */
const WINDOW_APPLIERS: {
  readonly [KindType in WindowSpec['kind']]: (
    ds: MaterializedDataset,
    spec: Extract<WindowSpec, { kind: KindType }>,
    outSchema: Schema,
    parts: readonly (readonly number[])[],
  ) => MaterializedDataset;
} = (() => {
  // ffill / bfill — pure null-skipping carry per partition via the shared
  // `fillColumnArray` SSOT, scattered back to source row order. Partitions
  // cover every row, and the kernel preserves observed cells, so the scatter
  // reconstructs the whole column.
  const applyFill = (
    ds: MaterializedDataset,
    spec: Extract<WindowSpec, { kind: 'ffill' | 'bfill' }>,
    outSchema: Schema,
    parts: readonly (readonly number[])[],
  ): MaterializedDataset => {
    const src = ds.columns[spec.col] ?? [];
    const filled = scatterParts(materializedRowCount(ds), parts, (part) =>
      fillColumnArray(
        part.map((i) => src[i] as Cell),
        spec.kind,
        spec.limit,
      ),
    );
    return new MaterializedDataset({ ...ds.columns, [spec.col]: filled }, outSchema, ds.identity);
  };

  // first-value / last-value — the first/last *present* cell in frame order,
  // broadcast across the whole partition.
  const applyValue = (
    ds: MaterializedDataset,
    spec: Extract<WindowSpec, { kind: 'first-value' | 'last-value' }>,
    outSchema: Schema,
    parts: readonly (readonly number[])[],
  ): MaterializedDataset => {
    const src = ds.columns[spec.col] ?? [];
    const out = scatterParts(materializedRowCount(ds), parts, (part) => {
      const ordered = spec.kind === 'first-value' ? part : [...part].reverse();
      const pick = ordered.map((i) => src[i] as Cell).find((c) => !isFillGap(c));
      const value = (pick ?? null) as Cell;
      return part.map(() => value);
    });
    return new MaterializedDataset({ ...ds.columns, [spec.out]: out }, outSchema, ds.identity);
  };

  // Column-producing "scan" ops — pure JS kernels over the frame-ordered axes.
  const applyScan = (
    ds: MaterializedDataset,
    spec: ScanWindowSpec,
    outSchema: Schema,
    parts: readonly (readonly number[])[],
  ): MaterializedDataset => {
    const src = (ds.columns[spec.col] ?? []) as readonly Cell[];
    const timeCol = spec.order === undefined ? undefined : ((ds.columns[spec.order.column] ?? []) as readonly Cell[]);
    const out = scatterParts(
      materializedRowCount(ds),
      parts,
      (part) => computeScan(spec, scanXs(spec, part, timeCol), scanYs(part, src)) as readonly Cell[],
    );
    return new MaterializedDataset({ ...ds.columns, [spec.out]: out }, outSchema, ds.identity);
  };

  // lag / lead — arquero's native window op via the bundler-safe
  // string-expression form (`op`/`$`/`d` resolve inside arquero's parser, not
  // JS scope, so minification-safe). A synthetic row-index column restores
  // input row order after arquero's `orderby` reshuffle; date-aware framing
  // reuses `compileSortKeys` (SSOT). The conditional verb steps compose as a
  // pipe of `(t) => t` transforms.
  const applyShift = (
    ds: MaterializedDataset,
    spec: Extract<WindowSpec, { kind: 'lag' | 'lead' }>,
    outSchema: Schema,
  ): MaterializedDataset => {
    const n = materializedRowCount(ds);
    const Row = '__wrow__';
    const grouped = spec.partitionBy.length > 0;
    const expr =
      spec.kind === 'lag'
        ? '(d, $) => op.lag(d[$.winCol], $.winOffset, $.winDefault)'
        : '(d, $) => op.lead(d[$.winCol], $.winOffset, $.winDefault)';
    const steps: readonly ((t: ColumnTable) => ColumnTable)[] = [
      (t) => (grouped ? t.groupby(...spec.partitionBy) : t),
      (t) =>
        spec.order === undefined
          ? t
          : t.orderby(
              ...compileSortKeys([{ column: spec.order.column, direction: 'asc', dateAware: spec.order.dateAware }]),
            ),
      (t) => t.derive({ [spec.out]: expr }),
      (t) => (grouped ? t.ungroup() : t),
    ];
    const base = aqTable({ ...ds.columns, [Row]: Array.from({ length: n }, (_, i) => i) }).params({
      winCol: spec.col,
      winOffset: spec.offset,
      winDefault: spec.default,
    }) as ColumnTable;
    const t = steps.reduce((acc, step) => step(acc), base);
    const derived = t.array(spec.out);
    const origRow = t.array(Row) as readonly number[];
    // Each arquero output row `p` carries `derived[p]` and originally sat at row
    // `origRow[p]`; scatter restores input order.
    const outArr = scatterParts(n, [origRow], (rows) => rows.map((_, p) => (derived[p] ?? null) as Cell));
    return new MaterializedDataset({ ...ds.columns, [spec.out]: outArr }, outSchema, ds.identity);
  };

  const scanEntries = Object.fromEntries(
    (Object.keys(SCAN_KERNELS) as ScanWindowSpec['kind'][]).map((kind) => [kind, applyScan] as const),
  ) as { readonly [KindType in ScanWindowSpec['kind']]: typeof applyScan };

  return {
    ffill: applyFill,
    bfill: applyFill,
    'first-value': applyValue,
    'last-value': applyValue,
    lag: applyShift,
    lead: applyShift,
    ...scanEntries,
  } as const;
})() satisfies {
  readonly [KindType in WindowSpec['kind']]: (
    ds: MaterializedDataset,
    spec: Extract<WindowSpec, { kind: KindType }>,
    outSchema: Schema,
    parts: readonly (readonly number[])[],
  ) => MaterializedDataset;
};

/**
 * Apply a resolved {@link WindowSpec} to a materialised dataset.
 *
 *   - `ffill`/`bfill`/`first-value`/`last-value` — pure null-skipping
 *     kernels (arquero's native window ops include nulls; these must
 *     skip them per the locked semantics).
 *   - scan ops (rolling/cumulative/rate) — pure JS series-window kernels.
 *   - `lag`/`lead` — arquero's native `op.lag`/`op.lead` via the
 *     bundler-safe string-expression form, grouped/ordered through a
 *     synthetic row-index column so the output keeps input row order.
 *
 * Dispatch is a {@link WINDOW_APPLIERS} lookup keyed by `spec.kind`
 * (mirrors {@link SCAN_KERNELS}/{@link JOIN_FNS}); the cast narrows the
 * union spec to the applier's arm, the same pattern as {@link computeScan}.
 *
 * `outSchema` is supplied by the verb (its `propagateSchema` SSOT) so
 * this boundary helper stays schema-light. The boundary rule holds: arquero
 * is imported only in this file.
 */
export const applyWindow = (ds: MaterializedDataset, spec: WindowSpec, outSchema: Schema): MaterializedDataset => {
  const parts = framedPartitions(ds, spec);
  const applier = WINDOW_APPLIERS[spec.kind] as (
    ds: MaterializedDataset,
    spec: WindowSpec,
    outSchema: Schema,
    parts: readonly (readonly number[])[],
  ) => MaterializedDataset;
  return applier(ds, spec, outSchema, parts);
};
